import React from "react";
import { Banknote, Scale, PieChart, TrendingUp } from "lucide-react";

import OverviewKpiCard from "../companyOverview/OverviewKpiCard";
import PerformanceMethodologyPopover from "./PerformanceMethodologyPopover";
import { money, pct } from "./performanceSelectors";

/**
 * The four headline figures of the selected month.
 *
 * ALL FOUR COME FROM ONE MONTH AND ONE REQUEST. `selected` is the month the tab
 * asked for; there is no second source and nothing here is combined across the
 * four cards. `OverviewKpiCard` is the company tabs' existing KPI tile and is
 * reused rather than re-styled, so a figure looks the same whichever tab a
 * reader came from — including its rule that an absent value renders "—" and
 * never "CHF 0".
 *
 * THE VORMONAT LINE IS A LABEL, NOT A COMPARISON
 * ----------------------------------------------
 * `previous` is on the wire, and the obvious thing to do with it is subtract.
 * This does not. Each subtext STATES the previous month's own figure, labelled
 * as such — no difference, no percentage change, no arrow and no colour saying
 * whether the change is good. Two reasons, and the second is the real one:
 *
 *   1. A delta computed here would be a second definition of growth, living in a
 *      browser, beside figures the backend defines exactly. When the two ever
 *      disagreed, the one on screen would win.
 *   2. A month-over-month change in these figures is frequently NOT a
 *      performance signal at all — a unit entering or leaving the lease-gated
 *      population moves revenue and cost together — and an arrow would present
 *      it as one.
 *
 * The reader gets both numbers and can see the direction themselves; what they
 * do not get is a claim the data cannot support.
 *
 * THE MARGIN CAN BE ABSENT AND THAT IS AN ANSWER. `operating_margin_pct` is null
 * — never 0 — when revenue is not positive, because a unit that earned nothing
 * has no measurable margin while 0 would read as break-even. The card shows "—".
 */

/**
 * "Vormonat: <figure>" / "Vorperiode: <figure>", or nothing when the comparison
 * carries no figure.
 *
 * P2: the word follows the WINDOW. Calling a six-month comparison "Vormonat" is
 * not a wording preference — it names the wrong period, and the reader has no
 * way to tell. An em-dash still suppresses the line entirely rather than
 * printing a label over nothing.
 */
function comparisonLine(formatted, isRange) {
  if (formatted === "—") return null;
  return `${isRange ? "Vorperiode" : "Vormonat"}: ${formatted}`;
}

export default function PerformanceKpiGrid({
  selected,
  previous,
  methodology,
  isRange = false,
  comparisonText = "",
  loading = false,
}) {
  const previousLine = (formatted) => comparisonLine(formatted, isRange);
  // Which window the comparison actually covers, stated once under the first
  // card rather than repeated four times. Only for a range: "Vormonat" already
  // names its own period unambiguously.
  const comparisonHint =
    isRange && comparisonText ? `Vorperiode: ${comparisonText}` : null;
  return (
    <div
      data-testid="performance-kpi-grid"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <OverviewKpiCard
        testId="performance-kpi-revenue"
        title={isRange ? "Umsatz" : "Monatsumsatz"}
        icon={<Banknote className="h-4 w-4" aria-hidden />}
        accent="blue"
        loading={loading}
        value={money(selected?.expected_recurring_revenue_chf)}
        subtext={
          <>
            {previousLine(money(previous?.expected_recurring_revenue_chf)) ? (
              <span className="block">
                {previousLine(money(previous?.expected_recurring_revenue_chf))}
              </span>
            ) : null}
            {comparisonHint ? (
              <span
                className="block"
                data-testid="performance-comparison-period"
              >
                {comparisonHint}
              </span>
            ) : null}
          </>
        }
      />

      <OverviewKpiCard
        testId="performance-kpi-result"
        title={
          <span className="inline-flex items-center gap-1.5">
            Erwartetes Objektergebnis
            <PerformanceMethodologyPopover methodology={methodology} />
          </span>
        }
        icon={<Scale className="h-4 w-4" aria-hidden />}
        accent="emerald"
        loading={loading}
        value={money(selected?.operating_result_chf)}
        subtext={
          <>
            {/* The cost side of the subtraction, kept visible. The figure the
                result is derived FROM is what makes it checkable, and it was on
                screen before this redesign; dropping it would leave the
                methodology popover as the only place the reader learns a cost
                basis exists at all. */}
            <span className="block">
              Objektkosten {money(selected?.operating_cost_total_chf)}
            </span>
            {previousLine(money(previous?.operating_result_chf)) ? (
              <span className="block">
                {previousLine(money(previous?.operating_result_chf))}
              </span>
            ) : null}
          </>
        }
      />

      <OverviewKpiCard
        testId="performance-kpi-occupancy"
        title="Auslastung"
        icon={<TrendingUp className="h-4 w-4" aria-hidden />}
        accent="sky"
        loading={loading}
        value={pct(selected?.monthly_occupancy_pct)}
        subtext={previousLine(pct(previous?.monthly_occupancy_pct))}
      />

      <OverviewKpiCard
        testId="performance-kpi-margin"
        title="Objektmarge"
        icon={<PieChart className="h-4 w-4" aria-hidden />}
        accent="violet"
        loading={loading}
        value={pct(selected?.operating_margin_pct)}
        subtext={previousLine(pct(previous?.operating_margin_pct))}
      />
    </div>
  );
}
